import { Controller } from "@hotwired/stimulus"
import { Chart, registerables } from "chart.js"

Chart.register(...registerables)

const TOOLTIP_POSITIVE = "#8ce99a"
const TOOLTIP_NEGATIVE = "#ffa8a8"

const COLORS = [
  "#ae3ec9",
  "#206bc4",
  "#2fb344",
  "#f76707",
  "#d63939",
  "#1098ad",
  "#fab005",
  "#4263eb"
]

const gaugeNeedlePlugin = {
  id: "gaugeNeedle",
  afterDraw(chart, _args, pluginOptions) {
    const dataset = chart.data.datasets[0] || {}
    const raw = pluginOptions?.score ?? dataset.needleValue ?? 0
    const score = Math.min(100, Math.max(0, Number(raw) || 0))
    const meta = chart.getDatasetMeta(0)
    const arc = meta?.data?.[0]
    if (!arc) return

    // ArcElement.x/y — центр doughnut; getCenterPoint() — середина сегмента
    const cx = arc.x
    const cy = arc.y
    const length = arc.outerRadius * 0.82
    // 0 → влево, 50 → вверх, 100 → вправо
    const angle = Math.PI + (Math.PI * score) / 100
    const ctx = chart.ctx

    ctx.save()
    ctx.translate(cx, cy)
    ctx.rotate(angle)
    ctx.beginPath()
    ctx.moveTo(-4, 0)
    ctx.lineTo(0, -3.5)
    ctx.lineTo(length, 0)
    ctx.lineTo(0, 3.5)
    ctx.closePath()
    ctx.fillStyle = "#1d273b"
    ctx.fill()
    ctx.beginPath()
    ctx.arc(0, 0, 5, 0, Math.PI * 2)
    ctx.fill()
    ctx.restore()
  }
}

export default class extends Controller {
  static targets = ["canvas"]
  static values = { config: Object }

  connect() {
    this.boundDestroy = this.destroyChart.bind(this)
    document.addEventListener("turbo:before-cache", this.boundDestroy)
    this.renderChart()
  }

  disconnect() {
    document.removeEventListener("turbo:before-cache", this.boundDestroy)
    this.destroyChart()
  }

  renderChart() {
    if (!this.hasCanvasTarget || !this.configValue) return

    const type = this.configValue.type || "bar"
    if (type === "gauge") {
      this.chart = this.buildGaugeChart()
    } else if (type === "doughnut") {
      this.chart = this.buildDoughnutChart()
    } else {
      this.chart = this.buildBarChart()
    }
  }

  buildBarChart() {
    const { labels, datasets, tooltip: tooltipMode } = this.configValue
    const hasSecondaryAxis = datasets.some((dataset) => dataset.yAxisID === "y1")
    const revenueSegmentsTooltip = tooltipMode === "revenue_segments"
    const usersSegmentsTooltip = tooltipMode === "users_segments"

    const scales = {
      x: {
        stacked: true,
        grid: { display: false }
      },
      y: {
        stacked: true,
        beginAtZero: true,
        ticks: {
          precision: 0,
          callback(value) {
            return Math.abs(value)
          }
        }
      }
    }

    if (hasSecondaryAxis) {
      scales.y1 = {
        position: "right",
        beginAtZero: true,
        grid: { drawOnChartArea: false },
        ticks: {
          precision: 0
        }
      }
    }

    let tooltip
    if (revenueSegmentsTooltip) {
      tooltip = {
        enabled: false,
        external: (context) => this.renderRevenueSegmentsTooltip(context)
      }
    } else if (usersSegmentsTooltip) {
      tooltip = {
        enabled: false,
        external: (context) => this.renderUsersSegmentsTooltip(context)
      }
    } else {
      tooltip = {
        callbacks: {
          label(context) {
            const label = context.dataset.label || ""
            const raw = context.parsed.y ?? 0
            const value = context.dataset.type === "line" ? raw : Math.abs(raw)
            return `${label}: ${value}`
          }
        }
      }
    }

    return new Chart(this.canvasTarget, {
      type: "bar",
      data: {
        labels,
        datasets: datasets.map((dataset, index) => {
          const color = dataset.backgroundColor || COLORS[index % COLORS.length]
          const isLine = dataset.type === "line"

          if (isLine) {
            return {
              type: "line",
              label: dataset.name,
              data: dataset.data,
              borderColor: dataset.borderColor || color,
              backgroundColor: color,
              borderWidth: 2,
              fill: false,
              pointRadius: 3,
              tension: 0.2,
              order: 0,
              yAxisID: dataset.yAxisID || "y",
              role: dataset.role,
              hidden: Boolean(dataset.hidden)
            }
          }

          const entry = {
            label: dataset.name,
            data: dataset.data,
            backgroundColor: color,
            borderWidth: 0,
            order: 1,
            role: dataset.role,
            hidden: Boolean(dataset.hidden)
          }
          if (dataset.stack) {
            entry.stack = dataset.stack
          }
          return entry
        })
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          mode: "index",
          intersect: false
        },
        plugins: {
          legend: {
            position: "bottom",
            labels: {
              filter(item, data) {
                const dataset = data.datasets[item.datasetIndex]
                const hiddenLegendRoles = ["acquired", "churn", "cac"]
                return !dataset?.hidden && !hiddenLegendRoles.includes(dataset?.role)
              }
            }
          },
          tooltip
        },
        scales
      }
    })
  }

  renderRevenueSegmentsTooltip(context) {
    const { chart, tooltip } = context
    const tooltipEl = this.ensureExternalTooltipEl(chart)

    if (tooltip.opacity === 0) {
      tooltipEl.style.opacity = "0"
      return
    }

    const dataIndex = tooltip.dataPoints?.[0]?.dataIndex
    if (dataIndex == null) {
      tooltipEl.style.opacity = "0"
      return
    }

    const title = tooltip.title?.[0] || chart.data.labels[dataIndex] || ""
    const datasets = chart.data.datasets
    const cashFlow = datasets.find((dataset) => dataset.role === "cash_flow")
    const mrrSeries = datasets.filter((dataset) => dataset.role === "mrr")
    const cacByName = Object.fromEntries(
      datasets
        .filter((dataset) => dataset.role === "cac")
        .map((dataset) => [dataset.label.replace(/ · привлечение$/, ""), dataset])
    )

    const rows = []
    const isStartMonth = dataIndex === 0

    if (cashFlow) {
      const value = Math.round(Number(cashFlow.data[dataIndex]) || 0)
      if (isStartMonth) {
        rows.push(`<div><span style="font-weight:600">Cash flow</span>: ${this.formatInt(value)}</div>`)
      } else {
        const cfColor = value > 0 ? TOOLTIP_POSITIVE : value < 0 ? TOOLTIP_NEGATIVE : "inherit"
        rows.push(`<div><span style="font-weight:600">Cash flow</span>: <span style="color:${cfColor}">${this.formatInt(value)}</span></div>`)
      }
    }

    mrrSeries.forEach((mrrDataset) => {
      const mrr = Math.round(Number(mrrDataset.data[dataIndex]) || 0)

      if (isStartMonth) {
        rows.push(`<div>${this.escapeHtml(mrrDataset.label)}: ${this.formatInt(mrr)}</div>`)
        return
      }

      const cacDataset = cacByName[mrrDataset.label]
      const cacAbs = Math.round(Math.abs(Number(cacDataset?.data[dataIndex]) || 0))
      const mrrHtml = `<span style="color:${mrr > 0 ? TOOLTIP_POSITIVE : "inherit"}">${this.formatInt(mrr)}</span>`
      const cacHtml = `<span style="color:${cacAbs > 0 ? TOOLTIP_NEGATIVE : "inherit"}">${cacAbs > 0 ? this.formatInt(cacAbs) : "0"}</span>`
      rows.push(`<div>${this.escapeHtml(mrrDataset.label)}: ${mrrHtml} / ${cacHtml}</div>`)
    })

    this.positionExternalTooltip(tooltipEl, chart, tooltip, title, rows)
  }

  renderUsersSegmentsTooltip(context) {
    const { chart, tooltip } = context
    const tooltipEl = this.ensureExternalTooltipEl(chart)

    if (tooltip.opacity === 0) {
      tooltipEl.style.opacity = "0"
      return
    }

    const dataIndex = tooltip.dataPoints?.[0]?.dataIndex
    if (dataIndex == null) {
      tooltipEl.style.opacity = "0"
      return
    }

    const title = tooltip.title?.[0] || chart.data.labels[dataIndex] || ""
    const datasets = chart.data.datasets
    const usersSeries = datasets.filter((dataset) => dataset.role === "users")
    const acquiredByName = Object.fromEntries(
      datasets
        .filter((dataset) => dataset.role === "acquired")
        .map((dataset) => [dataset.label.replace(/ · прирост$/, ""), dataset])
    )
    const churnByName = Object.fromEntries(
      datasets
        .filter((dataset) => dataset.role === "churn")
        .map((dataset) => [dataset.label.replace(/ · отток$/, ""), dataset])
    )

    let totalUsers = 0
    let totalAcquired = 0
    let totalChurn = 0
    const rows = []

    usersSeries.forEach((usersDataset) => {
      const users = Math.round(Number(usersDataset.data[dataIndex]) || 0)
      const acquired = Math.round(Number(acquiredByName[usersDataset.label]?.data[dataIndex]) || 0)
      const churnAbs = Math.round(Math.abs(Number(churnByName[usersDataset.label]?.data[dataIndex]) || 0))
      totalUsers += users
      totalAcquired += acquired
      totalChurn += churnAbs
      rows.push(`<div>${this.escapeHtml(usersDataset.label)}: ${this.formatUsersChange(users, acquired, churnAbs)}</div>`)
    })

    rows.push(`<div><span style="font-weight:600">Всего</span>: ${this.formatUsersChange(totalUsers, totalAcquired, totalChurn)}</div>`)

    this.positionExternalTooltip(tooltipEl, chart, tooltip, title, rows)
  }

  formatUsersChange(users, acquired, churnAbs) {
    const acquiredHtml = `<span style="color:${acquired > 0 ? TOOLTIP_POSITIVE : "inherit"}">${acquired > 0 ? `+${this.formatInt(acquired)}` : "0"}</span>`
    const churnHtml = `<span style="color:${churnAbs > 0 ? TOOLTIP_NEGATIVE : "inherit"}">${churnAbs > 0 ? `-${this.formatInt(churnAbs)}` : "0"}</span>`
    return `${acquiredHtml} / ${churnHtml} (${this.formatInt(users)})`
  }

  positionExternalTooltip(tooltipEl, chart, tooltip, title, rows) {
    tooltipEl.innerHTML = `
      <div style="font-weight:600;margin-bottom:4px">${this.escapeHtml(String(title))}</div>
      ${rows.join("")}
    `

    const { offsetLeft: positionX, offsetTop: positionY } = chart.canvas
    tooltipEl.style.opacity = "1"
    tooltipEl.style.left = `${positionX + tooltip.caretX}px`
    tooltipEl.style.top = `${positionY + tooltip.caretY}px`
  }

  ensureExternalTooltipEl(chart) {
    let tooltipEl = chart.canvas.parentNode.querySelector(".chartjs-external-tooltip")
    if (tooltipEl) return tooltipEl

    tooltipEl = document.createElement("div")
    tooltipEl.className = "chartjs-external-tooltip"
    Object.assign(tooltipEl.style, {
      background: "rgba(29, 39, 59, 0.92)",
      borderRadius: "4px",
      color: "#fff",
      opacity: "0",
      pointerEvents: "none",
      position: "absolute",
      transform: "translate(-50%, 0)",
      transition: "opacity 0.1s ease",
      fontSize: "12px",
      lineHeight: "1.4",
      padding: "8px 10px",
      zIndex: "10",
      whiteSpace: "nowrap"
    })
    chart.canvas.parentNode.style.position = "relative"
    chart.canvas.parentNode.appendChild(tooltipEl)
    return tooltipEl
  }

  formatInt(value) {
    return Math.round(Math.abs(value)).toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ")
  }

  formatSignedInt(value, { plus = false } = {}) {
    const rounded = Math.round(value)
    if (rounded === 0) return "0"
    const formatted = this.formatInt(rounded)
    if (rounded < 0) return `-${formatted}`
    return plus ? `+${formatted}` : formatted
  }

  escapeHtml(value) {
    return String(value)
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
  }

  buildDoughnutChart() {
    const { labels, datasets } = this.configValue
    const dataset = datasets[0] || { data: [] }
    const backgroundColor = dataset.backgroundColor ||
      dataset.data.map((_, index) => COLORS[index % COLORS.length])

    return new Chart(this.canvasTarget, {
      type: "doughnut",
      data: {
        labels,
        datasets: [
          {
            label: dataset.name,
            data: dataset.data,
            backgroundColor,
            borderWidth: 0,
            borderRadius: 4
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        cutout: "70%",
        plugins: {
          legend: {
            display: false
          }
        }
      }
    })
  }

  buildGaugeChart() {
    const { labels, datasets, score } = this.configValue
    const dataset = datasets[0] || { data: [] }
    const backgroundColor = dataset.backgroundColor ||
      dataset.data.map((_, index) => COLORS[index % COLORS.length])
    const value = Number(score) || 0

    return new Chart(this.canvasTarget, {
      type: "doughnut",
      data: {
        labels,
        datasets: [
          {
            label: dataset.name,
            data: dataset.data,
            backgroundColor,
            borderWidth: 0,
            borderRadius: 2,
            needleValue: value
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        circumference: 180,
        rotation: 270,
        cutout: "75%",
        plugins: {
          legend: { display: false },
          tooltip: { enabled: false },
          gaugeNeedle: { score: value }
        }
      },
      plugins: [gaugeNeedlePlugin]
    })
  }

  destroyChart() {
    if (this.chart) {
      const tooltipEl = this.chart.canvas?.parentNode?.querySelector(".chartjs-external-tooltip")
      tooltipEl?.remove()
      this.chart.destroy()
      this.chart = null
    }
  }
}
